import * as React from 'react'
import styled, { css } from 'styled-components'

import * as colors from '../constants/colors'
import * as bp from '../constants/breakpoints'
import * as glyphs from '../icons/glyphs'

interface TextAccordion {
  title: React.ReactNode
  content: React.ReactNode
}

export type TextAccordionProps = {
  className?: string
  items: Array<TextAccordion>
  overrideOpen?: boolean // overrideOpen is used to force all the items to be opened
}

export function TextAccordion({ items, className, overrideOpen }: TextAccordionProps) {
  const { isOpen, updateOpenIndices } = useIndice()

  return (
    <TextAccordionWrapper className={className} $overrideOpen={overrideOpen}>
      {items?.map((item, index) => (
        <TextAccordionItem key={index}>
          <Title
            onClick={() => {
              updateOpenIndices(index.toString())
            }}
          >
            <TitleFlex>
              {item.title}
              {!overrideOpen && (
                <>
                  {isOpen(index.toString()) ? (
                    <glyphs.stroke.CaretUp size={18} />
                  ) : (
                    <glyphs.stroke.CaretDown size={18} />
                  )}
                </>
              )}
            </TitleFlex>
          </Title>

          <Content $open={overrideOpen ? true : isOpen(index.toString())}>
            <ContentWrapper>{item.content}</ContentWrapper>
          </Content>
        </TextAccordionItem>
      ))}
    </TextAccordionWrapper>
  )
}

type OpenIndice = {
  isOpen: (index: string) => boolean
  updateOpenIndices: (index: string) => void
}

function useIndice(): OpenIndice {
  const [openIndices, setOpenIndices] = React.useState<Array<string>>([])

  const isOpen = (index: string): boolean => {
    return openIndices.findIndex((element) => element === index) !== -1
  }

  const updateOpenIndices = (index: string): void => {
    const activeIndex = openIndices.findIndex((element) => element === index)
    const updatedOpenIndices = [...openIndices]

    if (activeIndex >= 0) {
      updatedOpenIndices.splice(activeIndex, 1)
    } else {
      updatedOpenIndices.push(index)
    }

    setOpenIndices(updatedOpenIndices)
  }

  return {
    isOpen: (index: string) => isOpen(index),
    updateOpenIndices: (index: string) => updateOpenIndices(index),
  }
}

/** FIXME: Move this CSS into a separate style file when merging Accordion and TextAccordion. **/
const TextAccordionItem = styled.div`
  border-bottom: 1px solid ${colors.GREY_SHADE_5};

  &:last-child {
    border-bottom: 0px solid ${colors.PRIMARY_WHITE};
  }
`

const Content = styled.div<{ $open: boolean }>`
  max-height: ${({ $open }) => ($open ? 'fit-content' : '0')};
  opacity: ${({ $open }) => ($open ? '1' : '0')};
  overflow: hidden;
  transition: 0.8s ease;
`

const ContentWrapper = styled.div`
  white-space: pre-line;
  padding: 0 22px 24px 36px;
`

const Title = styled.div`
  cursor: pointer;
  padding: 24px 22px 24px 36px;
`

const TitleFlex = styled.div`
  align-items: center;
  display: flex;

  ${glyphs.stroke.CaretDown}, ${glyphs.stroke.CaretUp} {
    color: ${colors.GREY_SHADE_7};

    @media screen and ${bp.TABLET} {
      color: ${colors.PRIMARY_BLACK};
    }
  }
`

const TextAccordionWrapper = styled.div<{ $overrideOpen?: boolean }>`
  ${({ $overrideOpen }) => {
    if ($overrideOpen) {
      return css`
        ${Title}, ${ContentWrapper} {
          padding: 0;
        }
      `
    }
  }}
`
